import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Send, KeyRound } from 'lucide-react';
import { post } from '../../lib/apiClient';
import { API_URL } from '../../_config';
import { generateIdempotencyKey } from '../../lib/idempotency';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      await post(API_URL.AUTH.FORGOT_PASSWORD, { email }, {
        headers: { 'Idempotency-Key': generateIdempotencyKey() },
      });
      setSent(true);
    } catch (err: any) {
      setError(err.message ?? 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fp-wrap">
      <div className="fp-left">
        <div className="fp-left-dots" />
        <div className="fp-left-glow" />
        <div className="fp-left-inner">
          <Link to="/" className="fp-logo-link">
            <img src="/images/logo.png" alt="LeadNova" className="fp-logo" />
          </Link>
          <div className="fp-icon-box">
            <KeyRound size={36} color="#4DB8E8" />
          </div>
          <h1 className="fp-left-title">Don't worry, it happens</h1>
          <p className="fp-left-desc">
            We'll help you get back into your account in no time. Just enter the email you used to sign up.
          </p>
        </div>
      </div>

      <div className="fp-right">
        <div className="fp-right-inner">
          <div className="fp-mobile-logo">
            <Link to="/"><img src="/images/logo.png" alt="LeadNova" className="fp-logo-sm" /></Link>
          </div>
          {!sent ? (
            <>
              <div className="fp-form-header">
                <h2 className="fp-form-title">Reset your password</h2>
                <p className="fp-form-subtitle">Enter your email address and we'll send you a link to reset your password.</p>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="fp-field">
                  <label className="fp-label">Email address</label>
                  <input className="fp-input" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                {error && <div className="fp-error">{error}</div>}
                <button type="submit" className="fp-submit-btn" disabled={loading}>
                  <Send size={18} /> {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
              <div className="fp-back">
                <Link to="/login" className="fp-back-link">
                  <ArrowLeft size={16} /> Back to login
                </Link>
              </div>
            </>
          ) : (
            <div className="fp-success">
              <div className="fp-success-icon">
                <Mail size={28} color="var(--green)" />
              </div>
              <h2 className="fp-success-title">Check your email</h2>
              <p className="fp-success-desc">
                We've sent a password reset link to <strong>{email}</strong>. Please check your inbox.
              </p>
              <button className="fp-retry-btn" onClick={() => setSent(false)}>
                Didn't receive it? Try again
              </button>
              <Link to="/login" className="fp-back-link">
                <ArrowLeft size={16} /> Back to login
              </Link>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .fp-wrap { min-height: calc(100vh - 72px); display: flex; }

        .fp-left {
          flex: 0 0 50%; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #1A2D5A 0%, #243A6C 40%, #1c3562 100%);
          display: flex; flex-direction: column; justify-content: center; align-items: center;
          padding: 60px 56px;
        }
        .fp-left-dots {
          position: absolute; inset: 0; opacity: 0.05;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .fp-left-glow {
          position: absolute; bottom: -100px; right: -100px; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(77,184,232,0.15), transparent 70%);
          pointer-events: none;
        }
        .fp-left-inner { position: relative; z-index: 1; text-align: center; max-width: 400px; }
        .fp-logo-link { display: inline-block; margin-bottom: 48px; }
        .fp-logo { height: 56px; }
        .fp-icon-box {
          width: 80px; height: 80px; border-radius: 20px; margin: 0 auto 28px;
          background: rgba(77,184,232,0.15);
          display: flex; align-items: center; justify-content: center;
        }
        .fp-left-title { font-size: 32px; font-weight: 700; color: #fff; margin-bottom: 12px; }
        .fp-left-desc { font-size: 16px; color: rgba(255,255,255,0.6); line-height: 1.6; }

        .fp-right {
          flex: 0 0 50%; display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #fff;
        }
        .fp-right-inner { width: 100%; max-width: 440px; }
        .fp-mobile-logo { display: none; text-align: center; margin-bottom: 32px; }
        .fp-logo-sm { height: 44px; }

        .fp-form-header { margin-bottom: 32px; }
        .fp-form-title { font-size: 30px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .fp-form-subtitle { font-size: 16px; color: var(--gray-500); line-height: 1.5; }

        .fp-field { margin-bottom: 24px; }
        .fp-label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-900); margin-bottom: 6px; }
        .fp-input {
          width: 100%; height: 46px; padding: 0 14px; border-radius: 10px;
          border: 1px solid var(--gray-200); font-size: 15px; font-family: inherit;
          color: var(--gray-900); background: var(--gray-50); outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .fp-input:focus { border-color: var(--sky); background: #fff; box-shadow: 0 0 0 3px rgba(77,184,232,0.1); }

        .fp-submit-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; height: 48px; background: var(--green); color: #fff;
          border: none; border-radius: 10px; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: 0 4px 14px rgba(58,170,53,0.3); transition: all 0.2s;
          margin-bottom: 24px;
        }
        .fp-submit-btn:hover:not(:disabled) { background: #2e9229; }
        .fp-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .fp-error { background: #FFEBEE; color: var(--red); padding: 10px 14px; border-radius: 8px; font-size: 14px; margin-bottom: 16px; }

        .fp-back { text-align: center; }
        .fp-back-link {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 15px; font-weight: 500; color: var(--gray-500); text-decoration: none;
        }
        .fp-back-link:hover { color: var(--gray-900); text-decoration: none; }

        .fp-success { text-align: center; }
        .fp-success-icon {
          width: 64px; height: 64px; border-radius: 50%;
          background: var(--green-light); display: flex; align-items: center;
          justify-content: center; margin: 0 auto 24px;
        }
        .fp-success-title { font-size: 26px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .fp-success-desc { font-size: 16px; color: var(--gray-500); line-height: 1.6; margin-bottom: 32px; }
        .fp-success-desc strong { color: var(--gray-900); }
        .fp-retry-btn {
          display: block; width: 100%; padding: 12px 20px; border-radius: 10px;
          border: 1px solid var(--gray-200); background: #fff; font-size: 15px;
          font-weight: 500; color: var(--gray-900); cursor: pointer;
          font-family: inherit; margin-bottom: 16px; transition: all 0.2s;
        }
        .fp-retry-btn:hover { background: var(--gray-50); }

        @media (max-width: 768px) {
          .fp-wrap { flex-direction: column; }
          .fp-left { flex: none; padding: 32px 24px; }
          .fp-left-title { font-size: 24px; }
          .fp-left-desc { font-size: 14px; }
          .fp-icon-box { width: 60px; height: 60px; border-radius: 16px; margin-bottom: 20px; }
          .fp-logo-link { margin-bottom: 24px; }
          .fp-logo { height: 44px; }
          .fp-right { flex: none; padding: 32px 20px; }
        }

        @media (max-width: 480px) {
          .fp-left { display: none; }
          .fp-mobile-logo { display: block; }
          .fp-right { min-height: calc(100vh - 72px); padding: 40px 20px; }
          .fp-form-title { font-size: 26px; }
        }
      `}</style>
    </div>
  );
}
